import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import {
  BookOpenCheck,
  Plus,
  Loader2,
  ArrowLeft,
  HandHeart,
  Hash,
  BookOpen,
  ScrollText,
  Check,
  Minus,
  RotateCcw,
  Trash2,
  CheckCircle2,
  Send,
  X,
  ChevronRight,
  Sparkles,
  FileText,
  ExternalLink,
  Search,
  Library,
  CloudUpload,
  ChevronLeft,
  ZoomIn,
  ZoomOut,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { logAction } from '../../lib/audit';
import { uploadToCloudinary, isCloudinaryConfigured } from '../../lib/cloudinary';
import khassaidesData from '../../data/khassaides.json';

/* ------------------------------------------------------------------ */
/* Données & helpers                                                   */
/* ------------------------------------------------------------------ */

// URL du PDF d'un juz (médiathèque KhassidaEnPdf : 01-quran1-ar.pdf … 30-quran30-ar.pdf).
const juzPdfUrl = (n) => `https://khassidaenpdf.net/BOOKS/${String(n).padStart(2, '0')}-quran${n}-ar.pdf`;

// Reconnaît un fichier de juz (utilisé automatiquement, donc masqué du sélecteur d'édition).
const isJuzFile = (title) => /^\d{1,2}-quran\d{1,2}-ar$/i.test((title || '').trim());

// Plages de pages absolues des 30 juz (mushaf Madani, 604 pages).
const QURAN_JUZ_RANGES = [
  [1, 21], [22, 41], [42, 61], [62, 81], [82, 101], [102, 121], [122, 141],
  [142, 161], [162, 181], [182, 201], [202, 221], [222, 241], [242, 261],
  [262, 281], [282, 301], [302, 321], [322, 341], [342, 361], [362, 381],
  [382, 401], [402, 421], [422, 441], [442, 461], [462, 481], [482, 501],
  [502, 521], [522, 541], [542, 561], [562, 581], [582, 604]
];

// Chaque juz pointe vers son propre PDF ; les pages sont comptées au sein du juz.
const QURAN_JUZ = QURAN_JUZ_RANGES.map(([from, to], i) => ({
  order: i,
  label: `Juz ${i + 1}`,
  pageFrom: 1,
  pageTo: to - from + 1,
  pdfUrl: juzPdfUrl(i + 1)
}));

const getInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

const asDate = (raw) => {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  return new Date(raw);
};

const relativeDate = (raw) => {
  const d = asDate(raw);
  if (!d) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Pages lues / total pour une part à plage de pages.
const partProgress = (part) => {
  if (!part.pageFrom || !part.pageTo) {
    return { read: part.status === 'done' ? 1 : 0, total: 1, hasPages: false };
  }
  const total = part.pageTo - part.pageFrom + 1;
  const read = Math.min(total, Math.max(0, (part.currentPage ?? part.pageFrom - 1) - (part.pageFrom - 1)));
  return { read, total, hasPages: true };
};

const TYPES = {
  quran: { label: 'Khatmul Quran', icon: BookOpen, color: 'from-emerald-500 to-teal-600' },
  khassaide: { label: 'Khassaides', icon: ScrollText, color: 'from-indigo-500 to-violet-600' },
  count: { label: 'Récitations', icon: Hash, color: 'from-amber-500 to-orange-600' }
};

/* ------------------------------------------------------------------ */
/* Anneau de progression (SVG)                                         */
/* ------------------------------------------------------------------ */
function Ring({ value, size = 56, stroke = 6, className = '' }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <svg width={size} height={size} className={className}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-muted/30" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c - (pct / 100) * c}
        className="text-emerald-500 transition-all duration-500"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-foreground text-[11px] font-bold">
        {Math.round(pct)}%
      </text>
    </svg>
  );
}

/* ================================================================== */
/* Sélecteur d'ouvrage depuis la médiathèque (khassaides.json)         */
/* ================================================================== */
const SCOPE_CATS = {
  khassaide: ['Tous', 'Arabe', 'Wolof', 'Français', 'Anglais'],
  all: ['Tous', 'Khassaide', 'Quran', 'Arabe', 'Wolof', 'Français', 'Anglais'],
  quran: []
};

// scope: 'khassaide' (exclut le Coran) | 'quran' (éditions complètes du Coran) | 'all' (tout)
function MediathequePicker({ scope = 'khassaide', selected, onSelect }) {
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('Tous');
  const isQuran = scope === 'quran';
  const cats = SCOPE_CATS[scope] || [];

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    const out = [];
    for (const k of khassaidesData) {
      if (!k.pdfUrl) continue;
      const tags = (k.tags || []).map((t) => t.toLowerCase());
      const isQ = tags.includes('quran');
      // Périmètre : Coran (éditions complètes) / Khassaide (hors Coran) / tout.
      if (scope === 'quran' && (!isQ || isJuzFile(k.title))) continue;
      if (scope === 'khassaide' && isQ) continue;
      // Filtres par catégorie
      if (cat === 'Khassaide' && isQ) continue;
      if (cat !== 'Tous' && cat !== 'Khassaide' && !tags.includes(cat.toLowerCase())) continue;
      if (term) {
        const hay = `${k.title || ''} ${k.translation || ''}`.toLowerCase();
        if (!hay.includes(term)) continue;
      }
      out.push(k);
      if (out.length >= 80) break; // plafonné pour la perf
    }
    return out;
  }, [search, cat, scope]);

  return (
    <div className="space-y-2 rounded-xl border border-border/50 bg-muted/30 p-3">
      <div className="flex items-center gap-2">
        <Library className="h-4 w-4 text-emerald-600" />
        <p className="text-sm font-medium">{isQuran ? 'Choisir une édition complète du Coran' : 'Choisir dans la médiathèque'}</p>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={isQuran ? 'Rechercher une édition…' : 'Rechercher un ouvrage…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 pl-8"
        />
      </div>

      {cats.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                cat === c ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : 'border-border/60 text-muted-foreground hover:bg-muted'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="max-h-52 space-y-1 overflow-y-auto pr-1">
        {results.length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground">Aucun ouvrage trouvé.</p>
        ) : (
          results.map((k, i) => {
            const active = selected === k.pdfUrl;
            return (
              <button
                key={i}
                onClick={() => onSelect({ title: (k.title || '').trim(), pdfUrl: k.pdfUrl })}
                className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors ${
                  active ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40' : 'border-transparent hover:bg-muted'
                }`}
              >
                <FileText className={`h-4 w-4 shrink-0 ${active ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                <span className="min-w-0 flex-1 truncate" dir="auto">{(k.title || '').trim() || 'Sans titre'}</span>
                {active && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
              </button>
            );
          })
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">80 résultats max — affinez la recherche si besoin.</p>
    </div>
  );
}

/* ================================================================== */
/* Dialogue de création                                                */
/* ================================================================== */
function CreateReadingDialog({ open, onOpenChange, onCreated }) {
  const { user, userData } = useAuth();
  const [type, setType] = useState('quran');
  const [title, setTitle] = useState('');
  const [intention, setIntention] = useState('');
  const [saving, setSaving] = useState(false);

  // Coran : par juz (30) ou par nombre de parts égales
  const [quranMode, setQuranMode] = useState('juz'); // 'juz' | 'pages'
  const [quranParts, setQuranParts] = useState(10);
  const [quranPdf, setQuranPdf] = useState('');      // édition complète choisie (mode 'pages')
  const [quranSourceTitle, setQuranSourceTitle] = useState('');

  // Khassaide
  const [khassTitle, setKhassTitle] = useState('');
  const [khassPdf, setKhassPdf] = useState('');
  const [khassParts, setKhassParts] = useState(8);
  const [khassPages, setKhassPages] = useState('');
  const [hostCloud, setHostCloud] = useState(false); // héberger le PDF sur Cloudinary

  // Compteur
  const [target, setTarget] = useState(1000);
  const [unitLabel, setUnitLabel] = useState('récitations');
  const [countSource, setCountSource] = useState('');
  const [recitPdf, setRecitPdf] = useState('');      // ouvrage à réciter (médiathèque)
  const [recitSourceTitle, setRecitSourceTitle] = useState('');

  const reset = () => {
    setType('quran'); setTitle(''); setIntention('');
    setQuranMode('juz'); setQuranParts(10); setQuranPdf(''); setQuranSourceTitle('');
    setKhassTitle(''); setKhassPdf(''); setKhassParts(8); setKhassPages(''); setHostCloud(false);
    setTarget(1000); setUnitLabel('récitations'); setCountSource(''); setRecitPdf(''); setRecitSourceTitle('');
  };

  // Construit la liste des parts selon le type/mode
  const buildParts = () => {
    if (type === 'quran') {
      if (quranMode === 'juz') {
        // Édition complète choisie → pages absolues + PDF partagé ; sinon, un PDF par juz.
        if (quranPdf) {
          return QURAN_JUZ_RANGES.map(([from, to], i) => ({ order: i, label: `Juz ${i + 1}`, pageFrom: from, pageTo: to }));
        }
        return QURAN_JUZ.map((p) => ({ ...p }));
      }
      const n = Math.max(2, Math.min(60, Number(quranParts) || 10));
      const per = Math.ceil(604 / n);
      const parts = [];
      for (let i = 0; i < n; i++) {
        const from = i * per + 1;
        const to = Math.min(604, from + per - 1);
        if (from > 604) break;
        parts.push({ order: i, label: `Pages ${from}–${to}`, pageFrom: from, pageTo: to });
      }
      return parts;
    }
    if (type === 'khassaide') {
      const n = Math.max(1, Math.min(60, Number(khassParts) || 8));
      const totalPages = Number(khassPages) || 0;
      if (totalPages > 0) {
        const per = Math.ceil(totalPages / n);
        const parts = [];
        for (let i = 0; i < n; i++) {
          const from = i * per + 1;
          const to = Math.min(totalPages, from + per - 1);
          if (from > totalPages) break;
          parts.push({ order: i, label: `Pages ${from}–${to}`, pageFrom: from, pageTo: to });
        }
        return parts;
      }
      return Array.from({ length: n }, (_, i) => ({ order: i, label: `Partie ${i + 1}/${n}`, pageFrom: null, pageTo: null }));
    }
    return [];
  };

  const handleCreate = async () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) { toast.error('Donnez un titre à la lecture.'); return; }
    setSaving(true);
    try {
      const parts = buildParts();
      const isCount = type === 'count';

      // PDF au niveau de la lecture : khassaide, édition du Coran, ou ouvrage à réciter.
      let readingPdf = '';
      if (type === 'khassaide') readingPdf = khassPdf.trim();
      else if (type === 'quran') readingPdf = quranPdf.trim();
      else if (type === 'count') readingPdf = recitPdf.trim();
      // Hébergement Cloudinary optionnel (khassaide uniquement).
      if (type === 'khassaide' && hostCloud && readingPdf && isCloudinaryConfigured()) {
        try {
          const res = await uploadToCloudinary(readingPdf);
          readingPdf = res.secureUrl;
        } catch (e) {
          console.error('Hébergement Cloudinary échoué:', e);
          toast('Hébergement Cloudinary indisponible — le lien d\'origine est conservé.');
        }
      }

      const base = {
        title: cleanTitle,
        intention: intention.trim(),
        type,
        sourceTitle: type === 'khassaide' ? khassTitle.trim()
          : type === 'count' ? (recitSourceTitle.trim() || countSource.trim())
          : (quranSourceTitle.trim() || 'Saint Coran'),
        pdfUrl: readingPdf,
        status: 'open',
        partsTotal: isCount ? 0 : parts.length,
        partsDone: 0,
        target: isCount ? Math.max(1, Number(target) || 1) : parts.length,
        unitLabel: isCount ? (unitLabel.trim() || 'récitations') : 'parts',
        countCurrent: 0,
        createdBy: user.uid,
        createdByName: userData?.fullName || 'Membre',
        createdAt: serverTimestamp()
      };

      const ref = await addDoc(collection(db, 'collective_readings'), base);

      // Crée les parts en lot (Coran / Khassaide)
      if (!isCount && parts.length) {
        const batch = writeBatch(db);
        parts.forEach((p) => {
          const pRef = doc(collection(db, 'collective_readings', ref.id, 'parts'));
          batch.set(pRef, {
            order: p.order,
            label: p.label,
            pageFrom: p.pageFrom,
            pageTo: p.pageTo,
            pdfUrl: p.pdfUrl || null,
            assigneeId: null,
            assigneeName: null,
            status: 'free',
            currentPage: p.pageFrom ? p.pageFrom - 1 : 0,
            claimedAt: null,
            doneAt: null
          });
        });
        await batch.commit();
      }

      logAction(user.uid, user.email, 'CREATE_COLLECTIVE_READING', cleanTitle, { type, readingId: ref.id });
      toast.success('Lecture collective lancée 🤲');
      reset();
      onOpenChange(false);
      onCreated?.(ref.id);
    } catch (err) {
      console.error('Création lecture collective:', err);
      toast.error("Impossible de créer la lecture");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpenCheck className="h-5 w-5 text-emerald-600" />
            Nouvelle lecture collective
          </DialogTitle>
          <DialogDescription>
            Lancez un Khatm ou un wird partagé, dédié à une intention.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Type */}
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(TYPES).map(([key, t]) => {
              const Icon = t.icon;
              const active = type === key;
              return (
                <button
                  key={key}
                  onClick={() => setType(key)}
                  className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-xs font-medium transition-all ${
                    active ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : 'border-border/60 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Titre + intention */}
          <div className="space-y-2">
            <Input placeholder="Titre — ex : Khatm pour le Magal 2026" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Textarea
              placeholder="Intention / dédicace (optionnel) — ex : pour feu El Hadj…, pour la réussite de la communauté"
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              className="min-h-[60px] resize-none"
            />
          </div>

          {/* Options selon le type */}
          {type === 'quran' && (
            <div className="space-y-3 rounded-xl border border-border/50 bg-muted/30 p-3">
              <p className="text-sm font-medium">Découpage du Coran</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setQuranMode('juz')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${quranMode === 'juz' ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40' : 'border-border/60 hover:bg-muted'}`}
                >
                  Par Juz (30)
                </button>
                <button
                  onClick={() => setQuranMode('pages')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${quranMode === 'pages' ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40' : 'border-border/60 hover:bg-muted'}`}
                >
                  Par nombre de parts
                </button>
              </div>
              {quranMode === 'pages' && (
                <label className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Nombre de parts (604 pages réparties)</span>
                  <Input type="number" min={2} max={60} value={quranParts} onChange={(e) => setQuranParts(e.target.value)} className="w-24" />
                </label>
              )}

              {/* Édition du Coran depuis la médiathèque (optionnelle) */}
              <MediathequePicker
                scope="quran"
                selected={quranPdf}
                onSelect={({ title, pdfUrl }) => { setQuranSourceTitle(title); setQuranPdf(pdfUrl); }}
              />
              {quranPdf && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-300/70 bg-emerald-50/60 px-3 py-1.5 dark:border-emerald-900/40 dark:bg-emerald-950/10">
                  <FileText className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium" dir="auto">{quranSourceTitle || 'Édition sélectionnée'}</span>
                  <button onClick={() => { setQuranSourceTitle(''); setQuranPdf(''); }} className="shrink-0 text-muted-foreground hover:text-destructive" aria-label="Retirer">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                {quranMode === 'juz'
                  ? (quranPdf
                      ? 'Les 30 juz ouvriront cette édition à leur page.'
                      : 'Sans édition choisie : chaque juz utilise automatiquement son propre PDF de la médiathèque.')
                  : (quranPdf
                      ? 'Chaque part ouvrira cette édition à sa page.'
                      : 'Choisissez une édition pour pouvoir lire dans l\'app (sinon suivi par pages seulement).')}
              </p>
            </div>
          )}

          {type === 'khassaide' && (
            <div className="space-y-3">
              <MediathequePicker
                scope="khassaide"
                selected={khassPdf}
                onSelect={({ title, pdfUrl }) => { setKhassTitle(title); setKhassPdf(pdfUrl); }}
              />

              {/* Ouvrage sélectionné (nom éditable) ou saisie manuelle d'un lien */}
              {khassPdf ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-300/70 bg-emerald-50/60 px-3 py-1.5 dark:border-emerald-900/40 dark:bg-emerald-950/10">
                  <FileText className="h-4 w-4 shrink-0 text-emerald-600" />
                  <Input
                    value={khassTitle}
                    onChange={(e) => setKhassTitle(e.target.value)}
                    placeholder="Nom de l'ouvrage"
                    dir="auto"
                    className="h-8 flex-1 border-0 bg-transparent px-0 text-sm font-medium shadow-none focus-visible:ring-0"
                  />
                  <button onClick={() => { setKhassTitle(''); setKhassPdf(''); setHostCloud(false); }} className="shrink-0 text-muted-foreground hover:text-destructive" aria-label="Retirer">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Input
                  placeholder="…ou collez un lien PDF manuellement"
                  value={khassPdf}
                  onChange={(e) => setKhassPdf(e.target.value)}
                />
              )}

              <div className="flex gap-2">
                <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                  Nombre de parts
                  <Input type="number" min={1} max={60} value={khassParts} onChange={(e) => setKhassParts(e.target.value)} />
                </label>
                <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                  Total pages (optionnel)
                  <Input type="number" min={0} value={khassPages} onChange={(e) => setKhassPages(e.target.value)} placeholder="ex : 80" />
                </label>
              </div>

              {/* Hébergement Cloudinary (optionnel) */}
              {khassPdf && isCloudinaryConfigured() && (
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border/60 p-2.5 text-sm">
                  <input type="checkbox" checked={hostCloud} onChange={(e) => setHostCloud(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" />
                  <span className="flex items-center gap-1.5">
                    <CloudUpload className="h-4 w-4 text-emerald-600" />
                    <span className="text-muted-foreground">Héberger le PDF sur Cloudinary <span className="text-[11px]">(plus fiable pour les petits fichiers ; si le PDF ne s'affiche pas)</span></span>
                  </span>
                </label>
              )}
            </div>
          )}

          {type === 'count' && (
            <div className="space-y-3">
              <Input placeholder="Objet — ex : Matlaboul Fawzeyni (optionnel)" value={countSource} onChange={(e) => setCountSource(e.target.value)} />

              {/* Ouvrage à réciter depuis la médiathèque (optionnel) */}
              <MediathequePicker
                scope="all"
                selected={recitPdf}
                onSelect={({ title, pdfUrl }) => { setRecitSourceTitle(title); setRecitPdf(pdfUrl); if (!countSource.trim()) setCountSource(title); }}
              />
              {recitPdf && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-300/70 bg-emerald-50/60 px-3 py-1.5 dark:border-emerald-900/40 dark:bg-emerald-950/10">
                  <FileText className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium" dir="auto">{recitSourceTitle || 'Ouvrage sélectionné'}</span>
                  <button onClick={() => { setRecitSourceTitle(''); setRecitPdf(''); }} className="shrink-0 text-muted-foreground hover:text-destructive" aria-label="Retirer">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                  Objectif total
                  <Input type="number" min={1} value={target} onChange={(e) => setTarget(e.target.value)} />
                </label>
                <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                  Unité
                  <Input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} placeholder="récitations" />
                </label>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span className="ml-1.5">Lancer la lecture</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== */
/* Lecteur PDF intégré (pdf.js → rendu canvas, fallback iframe)        */
/* ================================================================== */

// Chargement paresseux & mémoïsé du moteur pdf.js (worker servi depuis /public).
let pdfjsPromise = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/build/pdf').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return pdfjs;
    });
  }
  return pdfjsPromise;
};

/**
 * Rendu canvas d'un PDF avec navigation et zoom.
 * onPageChange(absolutePage) est appelé à chaque changement de page (suivi de lecture).
 */
function PdfReader({ url, initialPage = 1, onPageChange }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const docRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(Math.max(1, initialPage || 1));
  const [scale, setScale] = useState(1);

  // Chargement du document
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    docRef.current = null;
    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        // Streaming : ne télécharge que les pages affichées (requêtes Range).
        // Efficace uniquement si l'hôte envoie CORS + Range (ex. Cloudinary).
        const task = pdfjs.getDocument({
          url,
          withCredentials: false,
          disableAutoFetch: true,
          disableStream: false,
          rangeChunkSize: 262144 // 256 Ko par tranche
        });
        const doc = await task.promise;
        if (cancelled) { try { doc.destroy(); } catch (e) { /* ignore */ } return; }
        docRef.current = doc;
        setNumPages(doc.numPages);
        const start = Math.min(Math.max(1, initialPage || 1), doc.numPages);
        setPageNum(start);
        // Ajuste l'échelle à la largeur disponible.
        try {
          const first = await doc.getPage(start);
          const vp = first.getViewport({ scale: 1 });
          const avail = (containerRef.current?.clientWidth || 760) - 24;
          setScale(Math.min(2.2, Math.max(0.4, avail / vp.width)));
        } catch (e) { /* échelle par défaut */ }
        setStatus('ready');
      } catch (e) {
        if (!cancelled) { console.warn('pdf.js: chargement impossible → fallback', e); setStatus('error'); }
      }
    })();
    return () => {
      cancelled = true;
      try { renderTaskRef.current?.cancel(); } catch (e) { /* ignore */ }
      try { docRef.current?.destroy(); } catch (e) { /* ignore */ }
    };
  }, [url, initialPage]);

  // Rendu de la page courante
  useEffect(() => {
    const doc = docRef.current;
    if (status !== 'ready' || !doc) return;
    let cancelled = false;
    (async () => {
      try {
        const pageObj = await doc.getPage(pageNum);
        if (cancelled) return;
        const dpr = window.devicePixelRatio || 1;
        const viewport = pageObj.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        try { renderTaskRef.current?.cancel(); } catch (e) { /* ignore */ }
        const task = pageObj.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined
        });
        renderTaskRef.current = task;
        await task.promise;
      } catch (e) {
        if (e?.name !== 'RenderingCancelledException') console.warn('pdf.js: rendu', e);
      }
    })();
    return () => { cancelled = true; };
  }, [status, pageNum, scale]);

  const goTo = useCallback((n) => {
    setPageNum((prev) => {
      const next = Math.max(1, Math.min(numPages || 1, n));
      if (next !== prev) onPageChange?.(next);
      return next;
    });
  }, [numPages, onPageChange]);

  if (status === 'error') {
    // Fallback : visionneuse navigateur (web) + ouverture externe (mobile).
    const src = `${url}#page=${pageNum}&toolbar=0`;
    return (
      <div className="relative flex h-full flex-col">
        <div className="flex items-center gap-2 border-b bg-amber-50/60 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">Lecture intégrée indisponible pour ce document (source distante). Affichage du navigateur — sur mobile, utilisez « Ouvrir ».</span>
        </div>
        <iframe src={src} title="PDF" className="min-h-0 flex-1 border-none bg-muted/20" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Barre d'outils */}
      <div className="flex items-center justify-between gap-2 border-b bg-background/80 px-3 py-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={status !== 'ready' || pageNum <= 1} onClick={() => goTo(pageNum - 1)} aria-label="Page précédente">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[84px] text-center text-sm font-medium tabular-nums">
            {status === 'ready' ? `${pageNum} / ${numPages}` : '—'}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={status !== 'ready' || pageNum >= numPages} onClick={() => goTo(pageNum + 1)} aria-label="Page suivante">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={status !== 'ready'} onClick={() => setScale((s) => Math.max(0.4, +(s - 0.2).toFixed(2)))} aria-label="Dézoomer">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={status !== 'ready'} onClick={() => setScale((s) => Math.min(3, +(s + 0.2).toFixed(2)))} aria-label="Zoomer">
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Zone de rendu */}
      <div ref={containerRef} className="relative min-h-0 flex-1 overflow-auto bg-muted/30">
        {status === 'loading' && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        <div className="flex min-h-full items-start justify-center p-3">
          <canvas ref={canvasRef} className="rounded-md shadow-md" dir="ltr" />
        </div>
      </div>
    </div>
  );
}

function PdfDialog({ open, onOpenChange, url, title, page, onPageChange, hosting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center justify-between space-y-0 border-b p-3">
          <DialogTitle className="flex min-w-0 items-center gap-2 text-base">
            <FileText className="h-5 w-5 shrink-0 text-primary" /> <span className="truncate">{title}</span>
          </DialogTitle>
          <Button variant="outline" size="sm" className="shrink-0 gap-2" disabled={!url} onClick={() => url && window.open(url, '_blank', 'noopener')}>
            <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">Ouvrir</span>
          </Button>
        </DialogHeader>
        <div className="min-h-0 flex-1">
          {hosting && !url ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium">Préparation du document…</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                Première lecture : le fichier est copié sur le serveur pour une lecture fluide. Les prochaines fois seront instantanées.
              </p>
            </div>
          ) : (
            open && url && <PdfReader url={url} initialPage={page} onPageChange={onPageChange} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== */
/* Carte d'une part (Coran / Khassaide)                                */
/* ================================================================== */
function PartRow({ reading, part, onOpenPdf }) {
  const { user, userData, isAdmin, isResponsable } = useAuth();
  const isStaff = isAdmin || isResponsable;
  const mine = part.assigneeId === user.uid;
  const { read, total, hasPages } = partProgress(part);
  const pct = total ? Math.round((read / total) * 100) : 0;
  const readingRef = doc(db, 'collective_readings', reading.id);
  const partRef = doc(db, 'collective_readings', reading.id, 'parts', part.id);

  const claim = async () => {
    try {
      await updateDoc(partRef, {
        assigneeId: user.uid,
        assigneeName: userData?.fullName || 'Membre',
        status: 'in_progress',
        claimedAt: serverTimestamp()
      });
    } catch (e) { console.error(e); toast.error('Part déjà prise ?'); }
  };

  const release = async () => {
    try {
      await updateDoc(partRef, {
        assigneeId: null, assigneeName: null, status: 'free',
        currentPage: part.pageFrom ? part.pageFrom - 1 : 0, claimedAt: null, doneAt: null
      });
      if (part.status === 'done') updateDoc(readingRef, { partsDone: increment(-1) }).catch(() => {});
    } catch (e) { console.error(e); toast.error('Action impossible'); }
  };

  const setPage = async (next) => {
    const clamped = Math.max(part.pageFrom - 1, Math.min(part.pageTo, next));
    try { await updateDoc(partRef, { currentPage: clamped, status: clamped >= part.pageTo ? 'in_progress' : 'in_progress' }); }
    catch (e) { console.error(e); }
  };

  const finish = async () => {
    try {
      const batch = writeBatch(db);
      batch.update(partRef, { status: 'done', currentPage: part.pageTo || part.currentPage || 0, doneAt: serverTimestamp() });
      batch.update(readingRef, { partsDone: increment(1) });
      await batch.commit();
      toast.success('Part terminée. BarakAllahou f’ik 🤲');
    } catch (e) { console.error(e); toast.error('Action impossible'); }
  };

  return (
    <div className={`rounded-xl border p-3 transition-colors ${
      part.status === 'done' ? 'border-emerald-300/70 bg-emerald-50/50 dark:border-emerald-900/40 dark:bg-emerald-950/10'
      : mine ? 'border-indigo-300/70 bg-indigo-50/40 dark:border-indigo-900/40 dark:bg-indigo-950/10'
      : 'border-border/60'
    }`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-semibold leading-tight">
            {part.status === 'done' && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
            {part.label}
          </p>
          {part.assigneeName ? (
            <p className="truncate text-xs text-muted-foreground">
              {part.assigneeName}{hasPages && part.status !== 'done' ? ` · page ${read}/${total}` : ''}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Libre</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {part.assigneeName && (
            <Avatar className="h-8 w-8">
              <AvatarFallback className={`text-[10px] font-semibold text-white ${part.status === 'done' ? 'bg-emerald-600' : 'bg-gradient-to-br from-indigo-500 to-violet-600'}`}>
                {getInitials(part.assigneeName)}
              </AvatarFallback>
            </Avatar>
          )}
          {part.status === 'free' && reading.status === 'open' && (
            <Button size="sm" className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={claim}>
              Prendre
            </Button>
          )}
        </div>
      </div>

      {/* Barre de progression (parts à pages) */}
      {hasPages && part.status !== 'free' && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      {/* Contrôles du propriétaire */}
      {mine && part.status !== 'done' && reading.status === 'open' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(part.pdfUrl || reading.pdfUrl) && (
            <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => onOpenPdf(part)}>
              <FileText className="h-3.5 w-3.5" /> <span className="ml-1">Lire</span>
            </Button>
          )}
          {hasPages && (
            <div className="flex items-center gap-1 rounded-lg border border-border/60 p-0.5">
              <button onClick={() => setPage((part.currentPage ?? part.pageFrom - 1) - 1)} className="rounded-md p-1.5 hover:bg-muted" aria-label="Page précédente">
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-[64px] text-center text-xs font-medium tabular-nums">
                p. {Math.max(part.pageFrom, Math.min(part.pageTo, part.currentPage || part.pageFrom))}
              </span>
              <button onClick={() => setPage((part.currentPage ?? part.pageFrom - 1) + 1)} className="rounded-md p-1.5 hover:bg-muted" aria-label="Page suivante">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Button size="sm" className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={finish}>
            <Check className="h-3.5 w-3.5" /> <span className="ml-1">J'ai terminé</span>
          </Button>
          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-muted-foreground" onClick={release} title="Libérer la part">
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Staff peut libérer une part assignée à autrui */}
      {isStaff && !mine && part.status !== 'free' && reading.status === 'open' && (
        <div className="mt-2">
          <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs text-muted-foreground" onClick={release}>
            <RotateCcw className="h-3 w-3" /> <span className="ml-1">Libérer</span>
          </Button>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Détail : mode Coran / Khassaide (parts)                             */
/* ================================================================== */
function PartsBoard({ reading }) {
  const { user, userData } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdf, setPdf] = useState(null); // { url, title, page }
  const [filter, setFilter] = useState('all'); // 'all' | 'free' | 'mine'

  useEffect(() => {
    const q = query(collection(db, 'collective_readings', reading.id, 'parts'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setParts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error(e); setLoading(false); });
    return unsub;
  }, [reading.id]);

  const doneCount = parts.filter((p) => p.status === 'done').length;
  const allDone = parts.length > 0 && doneCount === parts.length;

  // Clôture automatique quand toutes les parts sont finies.
  useEffect(() => {
    if (allDone && reading.status === 'open') {
      updateDoc(doc(db, 'collective_readings', reading.id), { status: 'completed', completedAt: serverTimestamp() }).catch(() => {});
    }
  }, [allDone, reading.status, reading.id]);

  const myFreePick = async () => {
    const free = parts.find((p) => p.status === 'free');
    if (!free) return;
    try {
      await updateDoc(doc(db, 'collective_readings', reading.id, 'parts', free.id), {
        assigneeId: user.uid, assigneeName: userData?.fullName || 'Membre', status: 'in_progress', claimedAt: serverTimestamp()
      });
      toast.success(`Vous avez pris : ${free.label}`);
    } catch (e) { console.error(e); }
  };

  const freeCount = parts.filter((p) => p.status === 'free').length;
  const mineCount = parts.filter((p) => p.assigneeId === user.uid).length;
  const pct = parts.length ? Math.round((doneCount / parts.length) * 100) : 0;
  const visibleParts = parts.filter((p) =>
    filter === 'free' ? p.status === 'free'
      : filter === 'mine' ? p.assigneeId === user.uid
        : true
  );

  // Ouvre une part : lecture directe de la source (pdf.js → fallback iframe si CORS).
  const openPdf = useCallback((p) => {
    const rawUrl = p.pdfUrl || reading.pdfUrl;
    if (!rawUrl) return;
    const canTrack = p.assigneeId === user.uid && p.status !== 'done'
      && reading.status === 'open' && p.pageFrom && p.pageTo;
    setPdf({
      partId: p.id,
      url: rawUrl,
      title: `${reading.sourceTitle} — ${p.label}`,
      // PDF dédié (juz) → page lue dans le juz ; document partagé → page absolue.
      page: p.pdfUrl
        ? Math.max(1, p.currentPage || 1)
        : Math.max(p.pageFrom, Math.min(p.pageTo, p.currentPage || p.pageFrom)),
      pageFrom: p.pageFrom,
      pageTo: p.pageTo,
      canTrack
    });
  }, [reading, user]);

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Résumé de progression : où en est la lecture d'un coup d'œil. */}
      <div className="rounded-2xl border border-border/60 bg-muted/30 p-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-2xl font-bold leading-none tabular-nums">
              {doneCount}<span className="text-base font-medium text-muted-foreground">/{parts.length}</span>
            </p>
            <p className="text-xs text-muted-foreground">parts terminées</p>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <p><span className="font-semibold text-emerald-600">{freeCount}</span> libre{freeCount > 1 ? 's' : ''}</p>
            {mineCount > 0 && <p><span className="font-semibold text-indigo-600">{mineCount}</span> à vous</p>}
          </div>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        {mineCount === 0 && freeCount > 0 && reading.status === 'open' && (
          <p className="mt-2 text-xs text-muted-foreground">
            Prenez une part, lisez-la depuis « Lire », puis marquez « J'ai terminé ».
          </p>
        )}
      </div>

      {/* Action principale : prendre automatiquement une part libre. */}
      {freeCount > 0 && reading.status === 'open' && (
        <Button onClick={myFreePick} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700">
          <HandHeart className="h-4 w-4" /> <span className="ml-1.5">Prendre une part libre</span>
        </Button>
      )}

      {/* Filtres : retrouver d'un geste les parts libres ou les siennes. */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: `Toutes (${parts.length})` },
          { id: 'free', label: `Libres (${freeCount})` },
          ...(mineCount > 0 ? [{ id: 'mine', label: `Mes parts (${mineCount})` }] : []),
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              filter === f.id
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'border-border/60 text-muted-foreground hover:bg-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste des parts (filtrée). */}
      {visibleParts.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {filter === 'free'
            ? 'Aucune part libre — tout est pris 🤲'
            : filter === 'mine'
              ? "Vous n'avez pas encore pris de part."
              : 'Aucune part.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {visibleParts.map((part) => (
            <PartRow key={part.id} reading={reading} part={part} onOpenPdf={openPdf} />
          ))}
        </div>
      )}

      {pdf && (
        <PdfDialog
          open={!!pdf}
          onOpenChange={(o) => !o && setPdf(null)}
          url={pdf.url}
          title={pdf.title}
          page={pdf.page}
          hosting={pdf.hosting}
          onPageChange={pdf.canTrack ? (n) => {
            // La page du lecteur correspond directement à la page suivie de la part.
            const clamped = Math.max(pdf.pageFrom, Math.min(pdf.pageTo, n));
            updateDoc(doc(db, 'collective_readings', reading.id, 'parts', pdf.partId), {
              currentPage: clamped, status: 'in_progress'
            }).catch(() => {});
          } : undefined}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/* Détail : mode Compteur de récitations                               */
/* ================================================================== */
function CounterBoard({ reading }) {
  const { user, userData } = useAuth();
  const [tallies, setTallies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'collective_readings', reading.id, 'tallies'), orderBy('count', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setTallies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error(e); setLoading(false); });
    return unsub;
  }, [reading.id]);

  const mine = tallies.find((t) => t.id === user.uid)?.count || 0;
  const current = reading.countCurrent || 0;
  const target = reading.target || 1;
  const pct = Math.min(100, Math.round((current / target) * 100));
  const reached = current >= target;

  const add = async (delta) => {
    if (reading.status !== 'open') return;
    if (delta < 0 && (mine <= 0 || current <= 0)) return;
    setBusy(true);
    try {
      await setDoc(doc(db, 'collective_readings', reading.id, 'tallies', user.uid), {
        userId: user.uid, userName: userData?.fullName || 'Membre', count: increment(delta), updatedAt: serverTimestamp()
      }, { merge: true });
      await updateDoc(doc(db, 'collective_readings', reading.id), { countCurrent: increment(delta) });
      if (current + delta >= target && reading.status === 'open') {
        updateDoc(doc(db, 'collective_readings', reading.id), { status: 'completed', completedAt: serverTimestamp() }).catch(() => {});
      }
    } catch (e) { console.error(e); toast.error('Action impossible'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {/* Compteur global */}
      <Card className="rounded-2xl border-amber-200/60 bg-gradient-to-br from-amber-50 to-orange-50 dark:border-amber-900/40 dark:from-amber-950/20 dark:to-orange-950/10">
        <CardContent className="p-5 text-center">
          <p className="text-4xl font-extrabold tabular-nums text-amber-700 dark:text-amber-300">
            {current.toLocaleString()} <span className="text-xl font-semibold text-muted-foreground">/ {target.toLocaleString()}</span>
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{reading.unitLabel}</p>
          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {reading.pdfUrl && (
            <Button variant="outline" size="sm" className="mt-4 rounded-lg" onClick={() => setPdfOpen(true)}>
              <FileText className="h-4 w-4" /> <span className="ml-1.5">Lire l'ouvrage</span>
            </Button>
          )}
        </CardContent>
      </Card>

      {reading.pdfUrl && (
        <PdfDialog open={pdfOpen} onOpenChange={setPdfOpen} url={reading.pdfUrl} title={reading.sourceTitle || 'Ouvrage'} page={1} />
      )}

      {/* Mes ajouts */}
      {reading.status === 'open' && !reached && (
        <div className="space-y-2">
          <p className="text-center text-sm text-muted-foreground">Ma contribution : <span className="font-semibold text-foreground">{mine}</span></p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="icon" className="h-11 w-11 rounded-full" disabled={busy || mine <= 0} onClick={() => add(-1)}>
              <Minus className="h-4 w-4" />
            </Button>
            {[1, 5, 10].map((d) => (
              <Button key={d} className="h-11 rounded-full bg-amber-600 px-5 text-base hover:bg-amber-700" disabled={busy} onClick={() => add(d)}>
                +{d}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Classement des contributeurs */}
      <div className="space-y-2">
        <p className="text-sm font-semibold">Contributeurs</p>
        {loading ? (
          <Skeleton className="h-12 rounded-xl" />
        ) : tallies.length === 0 ? (
          <p className="text-sm text-muted-foreground">Soyez le premier à contribuer.</p>
        ) : (
          tallies.map((t, i) => (
            <div key={t.id} className="flex items-center gap-3 rounded-xl border border-border/50 p-2.5">
              <span className="w-5 text-center text-sm font-bold text-muted-foreground">{i + 1}</span>
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-gradient-to-br from-amber-500 to-orange-600 text-[10px] font-semibold text-white">{getInitials(t.userName)}</AvatarFallback>
              </Avatar>
              <p className="flex-1 truncate text-sm font-medium">{t.userName}{t.id === user.uid && <span className="text-muted-foreground"> (moi)</span>}</p>
              <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-300">{(t.count || 0).toLocaleString()}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Vue détaillée d'une lecture                                         */
/* ================================================================== */
function ReadingDetail({ readingId, onBack }) {
  const { user, userData, isAdmin } = useAuth();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'collective_readings', readingId), (snap) => {
      setReading(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    }, (e) => { console.error(e); setLoading(false); });
    return unsub;
  }, [readingId]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Supprime les sous-collections connues puis le document.
      for (const sub of ['parts', 'tallies']) {
        const s = await getDocs(collection(db, 'collective_readings', readingId, sub));
        await Promise.all(s.docs.map((d) => deleteDoc(d.ref)));
      }
      await deleteDoc(doc(db, 'collective_readings', readingId));
      toast.success('Lecture supprimée');
      setConfirmOpen(false);
      onBack();
    } catch (e) {
      console.error(e);
      toast.error('Suppression impossible');
      setDeleting(false);
    }
  };

  const shareToWall = async () => {
    setPosting(true);
    try {
      const t = TYPES[reading.type]?.label || 'Lecture';
      await addDoc(collection(db, 'community_posts'), {
        authorId: user.uid,
        authorName: userData?.fullName || 'Membre',
        content: `🤲 La communauté a achevé « ${reading.title} » (${t})${reading.intention ? `\nIntention : ${reading.intention}` : ''}\nQu'Allah l'accepte. Amine.`,
        images: [],
        createdAt: serverTimestamp(),
        reactions: { '🤲': [], '❤️': [], '🌙': [] },
        commentCount: 0,
        pinned: false
      });
      toast.success('Partagé sur le Mur');
    } catch (e) { console.error(e); toast.error('Partage impossible'); }
    finally { setPosting(false); }
  };

  if (loading) return <Skeleton className="h-64 rounded-2xl" />;
  if (!reading) return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /> <span className="ml-1">Retour</span></Button>
      <p className="text-muted-foreground">Cette lecture n'existe plus.</p>
    </div>
  );

  const Icon = TYPES[reading.type]?.icon || BookOpen;
  const isCount = reading.type === 'count';
  const pct = isCount
    ? Math.min(100, Math.round(((reading.countCurrent || 0) / (reading.target || 1)) * 100))
    : Math.round(((reading.partsDone || 0) / (reading.partsTotal || 1)) * 100);
  const completed = reading.status === 'completed';

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-emerald-700">
        <ArrowLeft className="h-4 w-4" /> Toutes les lectures
      </button>

      {/* En-tête */}
      <Card className={`overflow-hidden rounded-2xl border-none bg-gradient-to-br ${TYPES[reading.type]?.color || 'from-emerald-500 to-teal-600'} text-white`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white/20 p-3"><Icon className="h-6 w-6" /></div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold leading-tight">{reading.title}</h2>
                  {completed && <Badge className="bg-white/25 text-white hover:bg-white/25">Accomplie</Badge>}
                </div>
                {reading.sourceTitle && <p className="text-sm text-white/80">{reading.sourceTitle}</p>}
                {reading.intention && (
                  <p className="mt-1 flex items-start gap-1.5 text-sm text-white/90">
                    <HandHeart className="mt-0.5 h-4 w-4 shrink-0" /> <span className="italic">« {reading.intention} »</span>
                  </p>
                )}
                <p className="mt-1 text-xs text-white/70">Lancée par {reading.createdByName} · {relativeDate(reading.createdAt)}</p>
              </div>
            </div>
            <div className="shrink-0 text-white"><Ring value={pct} className="text-white [&_text]:fill-white [&>circle:first-child]:text-white/30" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Bannière d'accomplissement */}
      {completed && (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-5 text-center dark:border-emerald-900/40 dark:bg-emerald-950/10 sm:flex-row sm:text-left">
          <Sparkles className="h-8 w-8 text-emerald-500" />
          <div className="flex-1">
            <p className="font-semibold">Lecture accomplie 🤲</p>
            <p className="text-sm text-muted-foreground">Qu'Allah accepte l'effort de chacun. Vous pouvez l'annoncer à la communauté.</p>
          </div>
          <Button onClick={shareToWall} disabled={posting} className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="ml-1.5">Partager sur le Mur</span>
          </Button>
        </div>
      )}

      {/* Corps selon le type */}
      {isCount ? <CounterBoard reading={reading} /> : <PartsBoard reading={reading} />}

      {/* Suppression : le créateur de la lecture ou un admin. */}
      {(isAdmin || reading.createdBy === user.uid) && (
        <div className="flex justify-end pt-2">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="h-4 w-4" /> <span className="ml-1.5">Supprimer la lecture</span>
          </Button>
        </div>
      )}

      <ConfirmDeleteDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!deleting) setConfirmOpen(o); }}
        onConfirm={handleDelete}
        loading={deleting}
        title="Supprimer cette lecture ?"
        description="La lecture, ses parts et la progression de chacun seront définitivement supprimées. Cette action est irréversible."
        itemName={reading.title}
        confirmText="Supprimer"
      />
    </div>
  );
}

/* ================================================================== */
/* Carte de la liste                                                   */
/* ================================================================== */
function ReadingCard({ reading, onOpen }) {
  const Icon = TYPES[reading.type]?.icon || BookOpen;
  const isCount = reading.type === 'count';
  const pct = isCount
    ? Math.min(100, Math.round(((reading.countCurrent || 0) / (reading.target || 1)) * 100))
    : Math.round(((reading.partsDone || 0) / (reading.partsTotal || 1)) * 100);
  const completed = reading.status === 'completed';

  return (
    <button onClick={onOpen} className="group w-full text-left">
      <Card className="rounded-2xl border-border/50 transition-all hover:border-emerald-300/70 hover:shadow-md">
        <CardContent className="flex items-center gap-4 p-4">
          <div className={`rounded-2xl bg-gradient-to-br ${TYPES[reading.type]?.color || 'from-emerald-500 to-teal-600'} p-3 text-white`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate font-semibold leading-tight">{reading.title}</p>
              {completed && <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">Accomplie</Badge>}
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {isCount
                ? `${(reading.countCurrent || 0).toLocaleString()} / ${(reading.target || 0).toLocaleString()} ${reading.unitLabel}`
                : `${reading.partsDone || 0} / ${reading.partsTotal || 0} parts`}
              {reading.intention ? ` · ${reading.intention}` : ''}
            </p>
          </div>
          <Ring value={pct} size={48} stroke={5} />
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </CardContent>
      </Card>
    </button>
  );
}

/* ================================================================== */
/* PAGE PRINCIPALE                                                     */
/* ================================================================== */
export default function LecturesCollectives() {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'collective_readings'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setReadings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error('Erreur lectures collectives:', e); setLoading(false); });
    return unsub;
  }, []);

  const open = useMemo(() => readings.filter((r) => r.status !== 'completed'), [readings]);
  const done = useMemo(() => readings.filter((r) => r.status === 'completed'), [readings]);

  if (activeId) {
    return (
      <div className="space-y-6 pb-10">
        <ReadingDetail readingId={activeId} onBack={() => setActiveId(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Héro */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-emerald-600 via-teal-600 to-indigo-700 p-4 md:p-6 text-white shadow-lg">
        <div className="absolute -right-6 -top-6 opacity-20 hidden sm:block"><BookOpenCheck className="h-36 w-36" /></div>
        <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl md:rounded-2xl bg-white/20 p-2.5 md:p-3"><BookOpenCheck className="h-6 w-6 md:h-7 md:w-7" /></div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Lectures collectives</h1>
              <p className="text-xs md:text-sm text-white/85 hidden sm:block">Khatm du Coran, Khassaides et wird partagés — chacun sa part, en temps réel.</p>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="shrink-0 rounded-xl bg-white text-emerald-700 hover:bg-white/90">
            <Plus className="h-4 w-4" /> <span className="ml-1.5">Nouvelle lecture</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : readings.length === 0 ? (
        <Card className="rounded-2xl border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <BookOpenCheck className="h-12 w-12 text-muted-foreground/40" />
            <p className="font-medium">Aucune lecture en cours</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Lancez un Khatm ou un wird collectif dédié à une intention — chacun pourra prendre sa part.
            </p>
            <Button onClick={() => setCreateOpen(true)} className="mt-2 rounded-xl bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4" /> <span className="ml-1.5">Lancer une lecture</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {open.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">En cours</h2>
              {open.map((r) => <ReadingCard key={r.id} reading={r} onOpen={() => setActiveId(r.id)} />)}
            </div>
          )}
          {done.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Accomplies</h2>
              {done.map((r) => <ReadingCard key={r.id} reading={r} onOpen={() => setActiveId(r.id)} />)}
            </div>
          )}
        </>
      )}

      <CreateReadingDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => setActiveId(id)} />
    </div>
  );
}
